import React, { useEffect, useState } from 'react';
import { User, fetchUsers, createUser, resetPassword } from '../utils/api';
import './UsersManager.css';

interface UsersManagerProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const UsersManager: React.FC<UsersManagerProps> = ({ userRole }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  // Create User form state
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'customer'>('customer');
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Password reset form state
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const loadUsersList = async () => {
    setLoading(true);
    try {
      const list = await fetchUsers();
      setUsers(list);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole !== 'customer') {
      loadUsersList();
    }
  }, [userRole]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    if (!newUsername.trim() || !newPassword.trim()) {
      setCreateError('Username and password are required');
      return;
    }

    try {
      await createUser({
        username: newUsername.trim(),
        password: newPassword,
        role: newUserRole,
      });
      setCreateSuccess(`✓ User "${newUsername}" created successfully`);
      setNewUsername('');
      setNewPassword('');
      loadUsersList();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create user');
    }
  };

  const handleResetPass = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (!oldPass || !newPass) {
      setResetError('Both fields are required');
      return;
    }

    try {
      await resetPassword(oldPass, newPass);
      setResetSuccess('✓ Password updated successfully');
      setOldPass('');
      setNewPass('');
    } catch (err: any) {
      setResetError(err.message || 'Failed to reset password');
    }
  };

  return (
    <div className="users-view-container">
      {/* Left: User Creation Form & List */}
      {userRole !== 'customer' && (
        <div className="users-panel-card">
          <div className="panel-header-section">
            <h4>👥 System User Accounts</h4>
            <p>Register new login credentials for developers or client accounts.</p>
          </div>

          <form onSubmit={handleCreateUser} className="users-form-body">
            <div className="form-group-custom">
              <label>Username / Login ID *</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="e.g. client_login"
                className="form-input-field"
              />
            </div>

            <div className="form-group-custom">
              <label>Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="form-input-field"
              />
            </div>

            <div className="form-group-custom">
              <label>Privilege Role</label>
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as any)}
                className="form-select-field"
              >
                <option value="customer">Customer (Reports & History Only)</option>
                <option value="admin">Administrator (Full Access)</option>
              </select>
            </div>

            {createError && <div className="form-error-alert-label">{createError}</div>}
            {createSuccess && <div className="form-success-alert-label">{createSuccess}</div>}

            <button type="submit" className="form-submit-action-btn">
              Create User Account
            </button>
          </form>

          <hr className="divider-line" />

          {/* Users List */}
          <div className="users-list-section">
            <h5>Current Accounts ({users.length})</h5>
            {loading ? (
              <div className="users-list-loader">Loading users...</div>
            ) : (
              <ul className="users-list-group">
                {users.map((u) => (
                  <li key={u.id} className="user-list-item-row">
                    <span className="user-list-name">{u.username}</span>
                    <span className={`role-badge ${u.role}`}>{u.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Right: Reset Password Form */}
      <div className="users-panel-card">
        <div className="panel-header-section">
          <h4>🔒 Change Account Password</h4>
          <p>Update your login password securely below.</p>
        </div>

        <form onSubmit={handleResetPass} className="users-form-body">
          <div className="form-group-custom">
            <label>Current Password</label>
            <input
              type="password"
              value={oldPass}
              onChange={(e) => setOldPass(e.target.value)}
              placeholder="••••••••"
              className="form-input-field"
            />
          </div>

          <div className="form-group-custom">
            <label>New Password</label>
            <input
              type="password"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              placeholder="••••••••"
              className="form-input-field"
            />
          </div>

          {resetError && <div className="form-error-alert-label">{resetError}</div>}
          {resetSuccess && <div className="form-success-alert-label">{resetSuccess}</div>}

          <button type="submit" className="form-submit-action-btn">
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default UsersManager;
